import React, { useState, useEffect } from 'react';
import './ManageDepartments.css';
import './DelegateManagement.css';
import { getDivisions, fetchDivisions, addDivision, removeDivision } from '../utils/departments';
import '../styles/ResizableTable.css';
import { initResizableTables } from '../utils/resizableTables';

const ManageDivisions: React.FC = () => {
  const [divisions, setDivisions] = useState<string[]>(getDivisions());
  const [newDiv, setNewDiv] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchDivisions().then(setDivisions);
  }, []);

  useEffect(() => {
    setTimeout(() => initResizableTables(), 50);
  }, [divisions]);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleAdd = async () => {
    const trimmed = newDiv.trim().toUpperCase();
    if (!trimmed) return;
    if (divisions.some((d) => d === trimmed)) {
      showMsg('error', 'Division already exists.');
      return;
    }
    setLoading(true);
    try {
      await addDivision(trimmed);
      setDivisions(await fetchDivisions());
      setNewDiv('');
      showMsg('success', `Division "${trimmed}" added.`);
    } catch (e: any) {
      showMsg('error', e?.response?.data?.detail || 'Failed to add division.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (div: string) => {
    if (!window.confirm(`Remove division "${div}"?`)) return;
    setLoading(true);
    try {
      await removeDivision(div);
      setDivisions(await fetchDivisions());
      showMsg('success', `Division "${div}" removed.`);
    } catch (e: any) {
      showMsg('error', e?.response?.data?.detail || 'Failed to remove division.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-depts-container">

      {message && (
        <div className={`message-alert ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="add-delegate-section">
        <h3>Add New Division</h3>
        <div className="add-delegate-form">
            <label htmlFor="div-input" className="dept-inline-label">Division Code:</label>
            <input
              id="div-input"
              type="text"
              placeholder="e.g. MOXXX"
              value={newDiv}
              onChange={(e) => setNewDiv(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="email-input"
              disabled={loading}
            />
          <button className="btn-add-delegate" onClick={handleAdd} disabled={loading}>
            {loading ? 'Saving...' : 'Add Division'}
          </button>
        </div>
      </div>

      <div className="delegates-list-section">
        <h3>Divisions ({divisions.length})</h3>
        <p className="manage-depts-desc">
          Divisions define routing for initiative approvals. Submitters select their division so approvers are scoped correctly.
        </p>

        {divisions.length === 0 ? (
          <div className="empty-state"><p>No divisions defined.</p></div>
        ) : (
          <div className="delegates-table-container">
          <table id="manage-divisions-table" className="delegates-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Division</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {divisions.map((div, idx) => (
                <tr key={div}>
                  <td>{idx + 1}</td>
                  <td><strong>{div}</strong></td>
                  <td>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemove(div)}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        <div className="manage-depts-footer">
        </div>
      </div>
    </div>
  );
};

export default ManageDivisions;
